import { CircularProgress, Stack, Typography } from '@mui/material'

import { FC } from 'react'
import { useTranslation } from 'react-i18next'

interface PhotoStudioSavingProps {
  /** Width of the component */
  width?: number | string
  /** Height of the component */
  height?: number | string
  /** Whether to show blur overlay background */
  showBlurOverlay?: boolean
  /** Preview image URL - when provided, component will be clipped to image boundaries */
  previewImageUrl?: string
  /** Custom text to display instead of default "Saving..." */
  text?: string
  /** Show loading spinner animation */
  showSpinner?: boolean
  /** Current processing step name (e.g., 'preparing', 'processing', 'finalizing') */
  step?: string | null
  /** Progress percentage (0-100) - only shown when step is 'processing' */
  progress?: number
}

/**
 * Photo Studio saving component that shows "Saving" text with blurred dark background
 * No animations, just text and blur overlay
 * 
 * @example
 * <PhotoStudioSaving showBlurOverlay={true} />
 */
export const PhotoStudioSaving: FC<PhotoStudioSavingProps> = ({
  width = '100%',
  height = '100%',
  showBlurOverlay = true,
  previewImageUrl,
  text,
  showSpinner = false,
  step,
  progress,
}) => {
  const { t } = useTranslation(['photo_studio_editing'])
  
  // Get step message if step is provided
  // Map step names to translation keys
  const stepTranslationMap: Record<string, string> = {
    'preparing': 'tool_switch_step_preparing',
    'processing': 'tool_switch_step_processing',
    'finalizing': 'tool_switch_step_finalizing',
  }
  
  const getStepMessage = (stepName: string | null): string | null => {
    if (!stepName) return null
    
    const translationKey = stepTranslationMap[stepName]
    if (!translationKey) return null
    
    const message = t(translationKey)
    // If translation returns the key itself, it wasn't found
    if (message === translationKey) return null
    
    return message
  }
  
  const stepMessage = getStepMessage(step ?? null)
  const displayText = text || stepMessage || t('saving_result')

  return (
    <Stack
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      width={width}
      height={height}
      alignItems="center"
      justifyContent="center"
      sx={{
        backgroundColor: showBlurOverlay ? 'rgba(37, 42, 46, 0.4)' : 'transparent', // GRAY_900 with 40% opacity
        zIndex: 1200,
        overflow: 'hidden', // Clip to parent container boundaries
        // Use CSS mask to clip to image boundaries when previewImageUrl is provided
        ...(previewImageUrl ? {
          maskImage: `url(${previewImageUrl})`,
          WebkitMaskImage: `url(${previewImageUrl})`,
          // Slightly oversize the mask to avoid 1px gaps from rounding
          maskSize: '102%',
          WebkitMaskSize: '102%',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskPosition: 'center',
        } : {}),
      }}
    >
      {showBlurOverlay && (
        <Stack
          position='absolute'
          top={0}
          left={0}
          right={0}
          bottom={0}
          width="100%"
          height="100%"
          sx={{
            backdropFilter: 'blur(4px)',
            // Apply same (slightly oversized) mask to blur layer
            ...(previewImageUrl ? {
              maskImage: `url(${previewImageUrl})`,
              WebkitMaskImage: `url(${previewImageUrl})`,
              maskSize: '102%',
              WebkitMaskSize: '102%',
              maskRepeat: 'no-repeat',
              WebkitMaskRepeat: 'no-repeat',
              maskPosition: 'center',
              WebkitMaskPosition: 'center',
            } : {}),
          }}
        />
      )}

      <Stack
        position='absolute'
        top={0}
        left={0}
        right={0}
        bottom={0}
        width="100%"
        height="100%"
        alignItems="center"
        justifyContent="center"
        sx={{
          zIndex: 1300,
          pointerEvents: 'none',
          // Apply same (slightly oversized) mask to content layer
          ...(previewImageUrl ? {
            maskImage: `url(${previewImageUrl})`,
            WebkitMaskImage: `url(${previewImageUrl})`,
            maskSize: '102%',
            WebkitMaskSize: '102%',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            maskPosition: 'center',
            WebkitMaskPosition: 'center',
          } : {}),
        }}
      >
        <Stack alignItems="center" spacing={2}>
          {/* Loading spinner */}
          {showSpinner && (
            <CircularProgress 
              size={40} 
              sx={{ 
                color: 'white',
                zIndex: 1301
              }} 
            />
          )}

          {/* Saving text with optional percentage for processing step */}
          <Typography 
            variant='text-md' 
            fontWeight='bold' 
            color="white" 
            sx={{ 
              zIndex: 1301,
              textAlign: 'center',
              maxWidth: '300px',
              // Ensure text wraps to 2 lines for smaller screens
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: 1.4,
            }}
          >
            {displayText}
            {step === 'processing' && progress !== undefined && (
              <span style={{ marginLeft: '0.5rem', opacity: 0.9 }}>
                {Math.round(Math.min(100, Math.max(0, progress)))}%
              </span>
            )}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  )
}
